import { css, StyleDeclarationValue } from 'aphrodite';
import { inject, observer } from 'mobx-react';
import * as React from 'react';
import { IImpersonationContextComponentProps, ImpersonationContextKey } from '../../../../models';
import { useErrorMessages } from '../../../../models/hooks/appStateHooks';
import { useModal } from '../../../../models/hooks/useModal';
import { useRemoveAccountLogoMutation, useUploadAccountLogoMutation } from '../../../../queries';
import { AccountOperationsViewModel, AdminPermission } from '../../../../viewmodels/AdminViewModels';
import { ConfirmationDialog, DefaultDeleteConfirmationOptions } from '../../../../web/components/ConfirmationDialog';
import { ImageFileChooserModal } from '../../../../web/components/ImageFileChooserModal';
import { LoadingSpinner } from '../../../../web/components/LoadingSpinner';
import { WarningIcon } from '../../../../web/components/svgs/icons/WarningIcon';
import { baseStyleSheet } from '../../../../web/styles/styles';
import { ConditionalAccess } from '../../ConditionalAccess';
import { AccountDetailsInfoField } from '../AccountDetailsInfoField';
import { styleSheet } from './styles';

interface IProps extends IImpersonationContextComponentProps {
	styles?: StyleDeclarationValue[];
	accountOperations: AccountOperationsViewModel;
}

export const AccountLogoField = inject(ImpersonationContextKey)(
	observer(function AccountLogoField({ accountOperations, impersonationContext, styles = [] }: IProps) {
		const errorMessages = useErrorMessages();
		const [showFileInputModal, setShowFileInputModal] = React.useState(false);
		const uploadLogoMutation = useUploadAccountLogoMutation({
			onSuccess: account => {
				if (account.preferences?.logo) {
					accountOperations.setAccountLogo(account.preferences.logo);
				}
				setShowFileInputModal(false);
			},
			onError: error => {
				setShowFileInputModal(false);
				errorMessages.pushApiError(error);
			},
		});

		const removeLogoMutation = useRemoveAccountLogoMutation({
			onSuccess: () => {
				accountOperations.setAccountLogo(null);
			},
			onError: error => {
				errorMessages.pushApiError(error);
			},
		});

		const deleteConfirmationModal = useModal(false, () => {
			removeLogoMutation.mutate({ impersonationContext });
		}, [removeLogoMutation, impersonationContext]);

		const onFileSelected = (logo: File) => {
			uploadLogoMutation.mutate({ logo, impersonationContext });
		};

		const onRemoveLogo = () => {
			deleteConfirmationModal.setIsOpen(true)();
		};

		const logoUrl = accountOperations.account?.preferences?.logo?.url;
		const isLoading = uploadLogoMutation.isPending || removeLogoMutation.isPending;

		const renderLogoLabel = () => {
			if (logoUrl) {
				return (
					<span>
						<span>Logo</span>
						&nbsp;
						{isLoading ? (
							<LoadingSpinner type='extra-tiny' className={css(baseStyleSheet.inlineBlock)} />
						) : (
							<>
								(
								<button
									className={css(baseStyleSheet.brandLink, styleSheet.editButton)}
									onClick={() => setShowFileInputModal(true)}
								>
									<span>Edit</span>
								</button>
								{' | '}
								<button className={css(baseStyleSheet.brandLink, styleSheet.editButton)} onClick={onRemoveLogo}>
									<span>Remove</span>
								</button>
								)
							</>
						)}
					</span>
				);
			}
			return 'Logo';
		};
		return (
			<AccountDetailsInfoField label={renderLogoLabel()} styles={[...styles]} valueStyles={[styleSheet.container]}>
				<ConditionalAccess permission={AdminPermission.ModifyAccount} elseChildren={null}>
					<>
						{logoUrl ? (
							<img className={css(styleSheet.accountLogo)} src={logoUrl} />
						) : (
							<button className={css(baseStyleSheet.brandLink)} onClick={() => setShowFileInputModal(true)}>
								<span>Upload Image</span>
							</button>
						)}
						<ImageFileChooserModal
							title='Upload Logo'
							modalProps={{
								isOpen: showFileInputModal,
								onRequestClose: (result, cancel) => {
									if (!cancel && result) {
										onFileSelected(result);
									} else {
										setShowFileInputModal(false);
									}
								},
							}}
						/>
						<ConfirmationDialog
							icon={<WarningIcon />}
							modalProps={deleteConfirmationModal}
							options={DefaultDeleteConfirmationOptions}
							title='Are you sure you want to remove the account logo?'
						/>
					</>
				</ConditionalAccess>
			</AccountDetailsInfoField>
		);
	})
);
